import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';

const API_BASE_URL = config.API_BASE_URL;
const SESSIONS_API_URL = `${API_BASE_URL}/api/v1/workout-sessions`;

const parseErrorDetail = (errorData, defaultMessage) => {
  if (!errorData || !errorData.detail) {
    return defaultMessage;
  }
  const { detail } = errorData;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0 && typeof detail[0] === 'object') {
    return detail
      .map(err => {
        const field = err.loc && err.loc.length > 1 ? `'${err.loc[err.loc.length - 1]}'` : 'A field';
        const message = err.msg || 'is invalid';
        const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
        return `${capitalizedField} ${message}`;
      })
      .join('. ');
  }
  try {
    const detailString = JSON.stringify(detail);
    if (detailString.length > 2) return detailString;
    return defaultMessage;
  } catch {
    return defaultMessage;
  }
};

/**
 * Fetches workout sessions for the current user.
 * Explicitly tags items with _isPartial if fields are limited.
 */
export const getWorkoutSessions = async (options = {}) => {
  const url = new URL(SESSIONS_API_URL);
  if (options.fields) {
    url.searchParams.append('fields', options.fields);
  }

  try {
    const response = await authService.authFetch(url.toString());
    if (!response.ok) throw new Error(`Failed to fetch workout sessions: ${response.status}`);
    
    const rawData = await response.json();
    
    // Handle both direct array and { items: [] } wrapper
    const items = Array.isArray(rawData) ? rawData : (rawData.items || rawData.data || []);

    if (options.fields) {
        console.log(`[workoutSessionApi] Fetched ${items.length} sessions with sparse fields. Tagging as partial.`);
        return items.map(item => ({ ...item, _isPartial: true }));
    }
    
    return items;
  } catch (error) {
    console.error('[workoutSessionApi] Error fetching sessions:', error);
    authService.trackNetworkError('getWorkoutSessions', error);
    throw error;
  }
};

export const getWorkoutSessionById = async (sessionId) => {
  const url = `${SESSIONS_API_URL}/${sessionId}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) throw new Error(`Failed to fetch session details: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error fetching single session:', error);
    authService.trackNetworkError('getWorkoutSessionById', error);
    throw error;
  }
};

export const createWorkoutSession = async (sessionData) => {
  const url = `${SESSIONS_API_URL}`;
  try {
    const response = await authService.authFetch(url, { method: 'POST', body: sessionData });
    if (!response.ok) throw new Error(`Failed to create workout session: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error creating session:', error);
    authService.trackNetworkError('createWorkoutSession', error);
    throw error;
  }
};

export const startSessionFromPlan = async (planId) => {
  const url = `${SESSIONS_API_URL}/from-plan/${planId}`;
  try {
    const response = await authService.authFetch(url, { method: 'POST' });
    if (!response.ok) throw new Error(`Failed to start session from plan: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error starting session from plan:', error);
    authService.trackNetworkError('startSessionFromPlan', error);
    throw error;
  }
};

export const updateWorkoutSession = async (sessionId, sessionData) => {
  const url = `${SESSIONS_API_URL}/${sessionId}`;
  try {
    const response = await authService.authFetch(url, { method: 'PUT', body: sessionData });
    if (!response.ok) throw new Error(`Failed to update workout session: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error updating session:', error);
    authService.trackNetworkError('updateWorkoutSession', error);
    throw error;
  }
};

export const patchWorkoutSession = async (sessionId, partialData) => {
  const url = `${SESSIONS_API_URL}/${sessionId}`;
  try {
    const response = await authService.authFetch(url, { method: 'PATCH', body: partialData });
    if (!response.ok) throw new Error(`Failed to patch workout session: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error patching session:', error);
    authService.trackNetworkError('patchWorkoutSession', error);
    throw error;
  }
};

export const deleteWorkoutSession = async (sessionId) => {
  const url = `${SESSIONS_API_URL}/${sessionId}`;
  try {
    const response = await authService.authFetch(url, { method: 'DELETE' });
    if (!response.ok) throw new Error(`Failed to delete workout session: ${response.status}`);
  } catch (error) {
    console.error('[workoutSessionApi] Error deleting session:', error);
    authService.trackNetworkError('deleteWorkoutSession', error);
    throw error;
  }
};

export const addActivityToSession = async (sessionId, activityData) => {
  const url = `${SESSIONS_API_URL}/${sessionId}/activities`;
  try {
    const response = await authService.authFetch(url, { method: 'POST', body: activityData });
    if (!response.ok) {
      const defaultMessage = `Failed to add activity: ${response.status}`;
      let finalError = defaultMessage;
      try {
        const errorData = await response.json();
        finalError = parseErrorDetail(errorData, defaultMessage);
      } catch (e) {  }
      throw new Error(finalError);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error adding activity to session:', error);
    authService.trackNetworkError('addActivityToSession', error);
    throw error;
  }
};

export const updateActivityInSession = async (sessionId, activityId, activityData) => {
  const url = `${SESSIONS_API_URL}/${sessionId}/activities/${activityId}`;
  try {
    const response = await authService.authFetch(url, { method: 'PUT', body: activityData });
    if (!response.ok) {
      const defaultMessage = `Failed to update activity: ${response.status}`;
      let finalError = defaultMessage;
      try {
        const errorData = await response.json();
        finalError = parseErrorDetail(errorData, defaultMessage);
      } catch (e) {  }
      throw new Error(finalError);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error updating activity in session:', error);
    authService.trackNetworkError('updateActivityInSession', error);
    throw error;
  }
};

export const removeActivityFromSession = async (sessionId, activityId) => {
  const url = `${SESSIONS_API_URL}/${sessionId}/activities/${activityId}`;
  try {
    const response = await authService.authFetch(url, { method: 'DELETE' });
    if (!response.ok) {
      const defaultMessage = `Failed to remove activity: ${response.status}`;
      let finalError = defaultMessage;
      try {
        const errorData = await response.json();
        finalError = parseErrorDetail(errorData, defaultMessage);
      } catch (e) {  }
      throw new Error(finalError);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutSessionApi] Error removing activity from session:', error);
    authService.trackNetworkError('removeActivityFromSession', error);
    throw error;
  }
};

export const getWorkoutSessionReport = async (sessionId) => {
  const url = `${SESSIONS_API_URL}/${sessionId}/report`;
  try {
    const response = await authService.authFetch(url);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      let errorDetail = `Failed to fetch workout session report: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = parseErrorDetail(errorData, errorDetail);
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const report = await response.json();
    return report;
  } catch (error) {
    if (!error.message?.includes('404')) {
        authService.trackNetworkError('getWorkoutSessionReport', error);
    }
    throw error;
  }
};